import React, { useState } from "react";
import {
  HelpCircle,
  Clock,
  CheckCircle2,
  RotateCcw,
  Send,
  User,
  ArrowRight,
  MessageSquare,
  FileText,
} from "lucide-react";
import { SmeTask } from "../../types";
import { ExpandableCard } from "../common/ExpandableCard";

interface SmeTaskCardProps {
  task: SmeTask;
  currentUserEmail?: string;
  onAccept?: (task: SmeTask) => void;
  onReturn?: (task: SmeTask, reason: string) => void;
  onSubmitResponse?: (task: SmeTask, response: string) => void;
  onStageIntoDraft?: (task: SmeTask) => void;
  showOpportunityContext?: boolean;
  defaultExpanded?: boolean;
}

export const SmeTaskCard: React.FC<SmeTaskCardProps> = ({
  task,
  currentUserEmail,
  onAccept,
  onReturn,
  onSubmitResponse,
  onStageIntoDraft,
  showOpportunityContext = false,
  defaultExpanded = false,
}) => {
  const [responseDraft, setResponseDraft] = useState(task.response || "");
  const [returnReason, setReturnReason] = useState("");
  const [isReturning, setIsReturning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isAssignee =
    currentUserEmail && task.assigneeEmail.toLowerCase() === currentUserEmail.toLowerCase();

  const getStatusBadge = () => {
    switch (task.status) {
      case "open":
        return {
          label: "Open / Pending SME",
          color: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300",
        };
      case "submitted":
        return {
          label: "Answer Submitted",
          color: "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300",
        };
      case "accepted":
        return {
          label: "Accepted",
          color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300",
        };
      case "returned":
        return {
          label: "Returned for Clarification",
          color: "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300",
        };
    }
  };

  const badge = getStatusBadge();

  const handleSubmitResponse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!responseDraft.trim() || !onSubmitResponse) return;
    setIsSubmitting(true);
    try {
      await onSubmitResponse(task, responseDraft.trim());
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmReturn = async () => {
    if (!returnReason.trim() || !onReturn) return;
    await onReturn(task, returnReason.trim());
    setIsReturning(false);
    setReturnReason("");
  };

  return (
    <ExpandableCard
      title={task.question}
      badge={badge.label}
      badgeColor={badge.color}
      defaultExpanded={defaultExpanded}
      summary={
        <div className="flex flex-wrap items-center gap-2 pt-1 text-xs text-slate-500">
          {showOpportunityContext && (
            <span className="font-semibold text-slate-700 dark:text-slate-300">
              {task.opportunityTitle} ({task.clientName})
            </span>
          )}
          <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700 dark:bg-slate-800 dark:text-slate-300">
            <User className="h-3 w-3 text-slate-400" />
            <span>SME: {task.assigneeName}</span>
          </span>
          {task.dueDate && (
            <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
              <Clock className="h-3 w-3" />
              <span>Due {task.dueDate}</span>
            </span>
          )}
          <span className="text-[11px] text-slate-400">Req: {task.requirementTitle}</span>
        </div>
      }
      details={
        <div className="space-y-4 text-xs">
          {/* Requirement Context */}
          <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 dark:border-slate-800 dark:bg-slate-900/40">
            <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              <FileText className="h-3.5 w-3.5" />
              <span>Target Requirement: {task.requirementTitle}</span>
            </div>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-300 line-clamp-3">
              {task.requirementText}
            </p>
          </div>

          {/* Question from Proposal Lead */}
          <div>
            <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              <HelpCircle className="h-3.5 w-3.5 text-[#2ba5b5]" />
              <span>Question from {task.requestedBy}:</span>
            </div>
            <blockquote className="mt-1 rounded-md border-l-2 border-[#2ba5b5] bg-teal-50/50 p-2.5 text-xs text-slate-800 dark:bg-teal-950/20 dark:text-slate-200">
              {task.question}
            </blockquote>
          </div>

          {/* Return Reason Banner if returned */}
          {task.status === "returned" && task.returnReason && (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/20 dark:text-rose-300">
              <span className="font-semibold">Returned for Clarification: </span>
              {task.returnReason}
            </div>
          )}

          {/* SME Response Section */}
          {task.status !== "open" && task.response && (
            <div>
              <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                <MessageSquare className="h-3.5 w-3.5 text-blue-500" />
                <span>
                  SME Answer from {task.assigneeName}{" "}
                  {task.respondedAt && `(${new Date(task.respondedAt).toLocaleDateString()})`}
                </span>
              </div>
              <div className="mt-1 whitespace-pre-wrap rounded-md border border-slate-200 bg-white p-3 text-xs leading-relaxed text-slate-800 shadow-2xs dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
                {task.response}
              </div>
            </div>
          )}

          {/* Response Form for Assignee */}
          {isAssignee && (task.status === "open" || task.status === "returned") && (
            <form
              onSubmit={handleSubmitResponse}
              className="space-y-2 border-t border-slate-100 pt-3 dark:border-slate-800"
            >
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                Your Answer / Technical Input:
              </label>
              <textarea
                rows={4}
                value={responseDraft}
                onChange={(e) => setResponseDraft(e.target.value)}
                placeholder="Provide specific technical figures, capacity commitments, or answers to the lead's question..."
                className="w-full rounded-lg border border-slate-300 p-2.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting || !responseDraft.trim()}
                  className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-[#238a97] disabled:opacity-50"
                >
                  <Send className="h-3.5 w-3.5" />
                  <span>{isSubmitting ? "Submitting..." : "Submit Answer to Proposal Team"}</span>
                </button>
              </div>
            </form>
          )}

          {/* Proposal Team Controls: Stage to Draft, Accept, Return */}
          {task.response && task.status === "submitted" && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3 dark:border-slate-800">
              <div className="flex items-center gap-2">
                {onStageIntoDraft && (
                  <button
                    type="button"
                    onClick={() => onStageIntoDraft(task)}
                    className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-[#2ba5b5] bg-teal-50 px-3 py-1.5 text-xs font-semibold text-[#0d7482] hover:bg-teal-100 dark:bg-teal-950/40 dark:text-teal-300"
                  >
                    <ArrowRight className="h-3.5 w-3.5" />
                    <span>Insert into Proposal Draft</span>
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2">
                {isReturning ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={returnReason}
                      onChange={(e) => setReturnReason(e.target.value)}
                      placeholder="Why is clarification needed?"
                      className="rounded-md border border-slate-300 p-1 text-xs dark:border-slate-700 dark:bg-slate-900"
                    />
                    <button
                      type="button"
                      onClick={handleConfirmReturn}
                      className="btn-animate rounded bg-rose-600 px-2 py-1 text-xs font-semibold text-white hover:bg-rose-700"
                    >
                      Confirm Return
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsReturning(false)}
                      className="text-xs text-slate-500 hover:underline"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    {onReturn && (
                      <button
                        type="button"
                        onClick={() => setIsReturning(true)}
                        className="btn-animate inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                      >
                        <RotateCcw className="h-3.5 w-3.5 text-slate-400" />
                        <span>Return for Revision</span>
                      </button>
                    )}
                    {onAccept && (
                      <button
                        type="button"
                        onClick={() => onAccept(task)}
                        className="btn-animate inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 shadow-2xs"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        <span>Accept Answer</span>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      }
    />
  );
};
